import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE, MaskedLines } from "../lib/motion";
import { useLang } from "../lib/i18n";
import { CywizText } from "./CywizMark";
import { CtaButton } from "./Shared";

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const reduce = useReducedMotion();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end md:justify-center overflow-hidden pt-24 pb-28 md:pb-0"
    >
      {/* Mobile : cadrage sur le portail (droite). Desktop : image complète centrée. */}
      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={reduce ? { duration: 0 } : { duration: 1.4, ease: EASE }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img
          src="/images/background-hero.webp"
          alt=""
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-[82%_40%] md:object-center"
        />
        {/* Mobile : voile bas pour le texte ; desktop : voile gauche léger */}
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/75 to-canvas/25 md:hidden" />
        <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-canvas/55 via-canvas/15 to-transparent max-w-4xl" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-canvas to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-site mx-auto px-6 md:px-12 w-full">
        <h1 className="hero-title font-display uppercase leading-[1.16] text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-cream">
          <MaskedLines
            lines={h.titleLines}
            lineClassName={(i, len) => (i === len - 1 ? "text-gradient" : "")}
          />
        </h1>

        {/* L'offre, directement sous la promesse. */}
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.8, ease: EASE, delay: 0.5 }}
          className="mt-6 max-w-xl font-sans text-base md:text-lg font-light text-warm leading-relaxed"
        >
          <CywizText>{h.subtitle}</CywizText>
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.8, ease: EASE, delay: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          {/* Cywiz d'abord : c'est le produit prêt, et la porte d'entrée
              commerciale du site. Le sur-mesure vient en second. */}
          <CtaButton
            data-testid="hero-cywiz-cta"
            href="#cywiz"
            className="group"
          >
            <CywizText>{h.primaryBtn}</CywizText>
            <ArrowUpRight size={16} aria-hidden className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </CtaButton>
          <CtaButton
            variant="outline"
            data-testid="hero-project-cta"
            href="#contact"
          >
            {h.secondaryBtn}
          </CtaButton>
        </motion.div>
      </div>

      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={reduce ? { duration: 0 } : { delay: 1.1, duration: 0.7 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-dim"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{h.scroll}</span>
        <ArrowDown size={16} className="motion-safe:animate-bounce" />
      </motion.div>
    </section>
  );
};
